/* Alerts — open alerts list. */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Pill, type PillTone } from '@sp/design-system-react';
import { useAlerts, type AlertRow } from '../hooks/useAlerts';

function severityTone(sev: AlertRow['severity']): PillTone {
  switch (sev) {
    case 'watch': return 'watch';
    case 'warn': return 'warn';
    case 'alarm': return 'alarm';
  }
}

function fmtAt(iso: string): string {
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return iso;
  return new Date(ts).toLocaleString();
}

export function AlertsPage(): JSX.Element {
  const [includeResolved, setIncludeResolved] = useState(false);
  const alerts = useAlerts({ includeResolved });

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-baseline justify-between">
        <h1 className="text-h1" style={{ color: 'var(--ink-primary)' }}>
          Alerts
        </h1>
        <div className="flex items-center gap-3">
          <span className="text-caption" style={{ color: 'var(--ink-tertiary)' }}>
            {alerts.data?.length ?? 0} {includeResolved ? 'total' : 'open'}
          </span>
          <Button
            variant={includeResolved ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setIncludeResolved((v) => !v)}
          >
            {includeResolved ? 'Hide resolved' : 'Show resolved'}
          </Button>
        </div>
      </header>

      <Card variant="surface" density="dense">
        {alerts.isLoading && (
          <div className="p-6 text-center text-body" style={{ color: 'var(--ink-tertiary)' }}>
            Loading…
          </div>
        )}
        {alerts.isError && (
          <div className="p-6 text-center text-body" style={{ color: 'var(--signal-alarm)' }}>
            Failed to load alerts.
          </div>
        )}
        {alerts.data && alerts.data.length === 0 && (
          <div className="p-6 text-center text-body" style={{ color: 'var(--ink-tertiary)' }}>
            No alerts.
          </div>
        )}
        {alerts.data && alerts.data.length > 0 && (
          <ul className="flex flex-col">
            {alerts.data.map((a) => (
              <li
                key={a.alert_id}
                className="flex items-start justify-between gap-4 px-3 py-3"
                style={{ borderBottom: '1px solid var(--border-hairline)' }}
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Pill tone={severityTone(a.severity)} label={a.severity} size="sm" />
                    <span className="text-body-strong" style={{ color: 'var(--ink-primary)' }}>
                      {a.alert_type}
                    </span>
                  </div>
                  <span className="text-caption" style={{ color: 'var(--ink-secondary)' }}>
                    {a.message}
                  </span>
                  <Link
                    to={`/devices/${a.device_id}`}
                    className="text-caption hover:underline"
                    style={{ color: 'var(--ink-tertiary)' }}
                  >
                    {a.device_name ?? a.device_id}
                  </Link>
                </div>
                <div className="text-right text-caption" style={{ color: 'var(--ink-tertiary)' }}>
                  <div>raised {fmtAt(a.raised_at)}</div>
                  {a.resolved_at && <div>resolved {fmtAt(a.resolved_at)}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
